'use client'

import { useState } from 'react'
import { usePathname } from 'lib/i18n/routing'
import { IconFacebook24 } from '@hashicorp/flight-icons/svg-react/facebook-24'
import { IconLink24 } from '@hashicorp/flight-icons/svg-react/link-24'
import { IconLinkedin24 } from '@hashicorp/flight-icons/svg-react/linkedin-24'
import { IconTwitterX24 } from '@hashicorp/flight-icons/svg-react/twitter-x-24'
import { m } from 'framer-motion'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import s from './style.module.css'

interface BlogPostSocialShareProps {
	summary: string
}

export default function BlogPostSocialShare({
	summary,
}: BlogPostSocialShareProps) {
	const pathname = usePathname()

	const [isTooltipActive, setIsTooltipActive] = useState(false)
	const [isTooltipTextPresent, setIsTooltipTextPresent] = useState(false)
	const url = `https://www.hashicorp.com${pathname}`

	const tooltipVariants = {
		active: { opacity: 1, y: -10, scale: 1 },
		inactive: { opacity: 0, y: 0, scale: 0.85 },
	}

	const tooltipCheckVariants = {
		inactive: {
			pathOffset: 1,
			pathLength: 1,
		},
		active: {
			pathOffset: 0,
			pathLength: 1,
		},
	}

	async function copyUrl() {
		if (isTooltipActive || isTooltipTextPresent) {
			return
		}

		try {
			await navigator.clipboard.writeText(url)
			setIsTooltipActive(true)
			setIsTooltipTextPresent(true)
			setTimeout(() => {
				setIsTooltipActive(false)
			}, 2000)
			setTimeout(() => {
				setIsTooltipTextPresent(false)
			}, 2250)
		} catch (err) {
			console.log(err)
		}
	}

	function openShare(url) {
		window.open(url, '_blank', 'popup, width=550, height=700')
	}

	return (
		<ul className={s.blogPostSocialShare}>
			<li>
				<button
					className={s.shareButton}
					onClick={() => {
						openShare(
							`https://twitter.com/intent/tweet?text=${
								summary || ''
							}&url=${encodeURIComponent(url)}`
						)
					}}
				>
					<VisuallyHidden>Twitter share</VisuallyHidden>
					<IconTwitterX24 />
				</button>
			</li>
			<li>
				<button
					className={s.shareButton}
					onClick={() => {
						openShare(
							`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
								url
							)}`
						)
					}}
				>
					<VisuallyHidden>LinkedIn share</VisuallyHidden>
					<IconLinkedin24 />
				</button>
			</li>
			<li>
				<button
					className={s.shareButton}
					onClick={() => {
						openShare(
							`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
								url
							)}`
						)
					}}
				>
					<VisuallyHidden>Facebook share</VisuallyHidden>
					<IconFacebook24 />
				</button>
			</li>
			<li>
				<button
					className={s.shareButton}
					onClick={async () => {
						await copyUrl()
					}}
					aria-controls="copy-url-tooltip"
				>
					<VisuallyHidden>Copy URL</VisuallyHidden>
					<IconLink24 />
				</button>
				<m.div
					id="copy-url-tooltip"
					initial={{ opacity: 0 }}
					animate={isTooltipActive ? 'active' : 'inactive'}
					variants={tooltipVariants}
					transition={{
						duration: 0.25,
					}}
					className={s.tooltip}
					aria-live="polite"
					role="status"
				>
					{isTooltipTextPresent ? <>Copied </> : null}
					<svg
						width="16"
						height="16"
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<m.path
							d="M20 6 9 17l-5-5"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							animate={isTooltipActive ? 'active' : 'inactive'}
							variants={tooltipCheckVariants}
							transition={{
								delay: 0.15,
							}}
						/>
					</svg>
				</m.div>
			</li>
		</ul>
	)
}
